



























import { computed, defineComponent, ref } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { InputAiUserQuestionaryUpdate } from "@scrile/api-provider/dist/projects/scrileConnect/AiUserQuestionaryProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { useAiUserSettings } from "@/hooks/useAiUserSettings";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EVENTS } from "@/const";
import BasePage from "@/components/BasePage";
import AiQuestionaryEdit from "./components/AiQuestionaryEdit";
import AiQuestionaryOnboarding from "./components/AiQuestionaryOnboarding";
import AiQuestionaryView from "./components/AiQuestionaryView";
import { QuestionnaireBlock } from "./config";

export default defineComponent({
  name: "PageAiQuestionary",
  components: {
    AiQuestionaryView,
    AiQuestionaryEdit,
    AiQuestionaryOnboarding,
    BasePage,
  },
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { root }) {
    const { isAdmin } = useAuthUser();
    const { aiAllowed, aiResponseEnabled, getAiUserSettings, updateAiUserSettings } = useAiUserSettings();

    const loading = ref(false);
    const values = ref<InputAiUserQuestionaryUpdate["fields"]>();
    const showOnboarding = ref(false);
    const blockForEdit = ref<QuestionnaireBlock | null>(null);
    const user = ref<UserPublicData | null>(null);
    const userNsfwEnabled = computed(() => (user.value?.nsfwSettings ? !user.value?.nsfwSettings.safeForWork : false));

    const emitMessage = () =>
      emitter.emit(EVENTS.NOTIFICATION_SHOW, {
        text: $t("Changes saved"),
        timeout: 1500,
      });

    const save = async (newValues: InputAiUserQuestionaryUpdate["fields"], cb?: () => void) => {
      values.value = { ...values.value, ...newValues };
      await providers.AiUserQuestionaryProvider.update({
        data: { userId: props.userId, fields: values.value },
      });
      emitMessage();
      cb && cb();
    };

    const checkAvailability = async () => {
      if (isAdmin.value) return;
      await getAiUserSettings();
      if (!aiAllowed.value) {
        root.$router.replace({ name: "home" });
      }
    };

    const handleUpdate = debounce(async ({ aiResponseEnabled }: { aiResponseEnabled: boolean }) => {
      if (loading.value) return;
      await updateAiUserSettings({ aiResponseEnabled });
      emitMessage();
    }, 500);

    const getAiQuestionary = async () => {
      const response = await providers.AiUserQuestionaryProvider.search({ userId: props.userId });
      response?.fields && Object.values(response.fields).some(value => !!value)
        ? (values.value = response.fields)
        : (showOnboarding.value = true);
    };

    const getUser = async () => {
      const response = await providers.UserPublicDataProvider.search({ data: { id: [props.userId] } });
      user.value = response.result[0] ?? null;
    };

    const getData = async () => {
      try {
        loading.value = true;
        await Promise.all([getAiQuestionary(), getUser()]);
      } finally {
        loading.value = false;
      }
    };

    checkAvailability();
    getData();

    return {
      aiResponseEnabled,
      loading,
      values,
      showOnboarding,
      blockForEdit,
      userNsfwEnabled,
      save,
      handleUpdate,
    };
  },
});
